import React, { useContext } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { CargoGroupContext } from "features/cargo-groups/CargoGroupProvider";
import { selectManyCargoGroups } from "features/cargo-groups/store/cargoGroupsSlice";
import { useCargoGroupsSliceSelector } from "features/cargo-groups/store/cargoGroupsStore";
import { FormProvider, useForm } from "react-hook-form";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";
import { UnloadingDetailsEnum } from "types/UnloadingDetails";

import { useFinalizeAndSendQr } from "../_hooks/useFinalizeAndSendQr";
import { useQuoteRequestSelector } from "../_store";
import { QuoteStepIndex } from "../types";
import PartnersStep from "./_components/PartnersStep";
import ReviewStep from "./_components/ReviewStep";
import { RoutingStep } from "./_components/RoutingStep";
import ShipmentDetailsStep from "./_components/ShipmentDetailsStep";
import TeamStep from "./_components/TeamMembersStep";
import {
  CargoGroup,
  // CargoGroup,
  FinalSchema,
  LocationSchema,
  locationSchema,
  partnersSchema,
  shipmentDetailsSchema,
  teamSchema,
} from "./_schemas";

const schemaMap = {
  [QuoteStepIndex.Routing]: locationSchema,
  [QuoteStepIndex.Shipment]: shipmentDetailsSchema,
  [QuoteStepIndex.Partners]: partnersSchema,
  [QuoteStepIndex.Team]: teamSchema,
  [QuoteStepIndex.Review]: teamSchema,
};

export const QuoteRequestDrayageForm: React.FC = ({}) => {
  const { quoteRequest: airQuoteRequest, stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { finalizeAndSendQr, resetLocalState } = useFinalizeAndSendQr();

  const { topLevelCargoGroupIds } = useContext(CargoGroupContext);

  const cargoGroups = useCargoGroupsSliceSelector((state) => selectManyCargoGroups(state, topLevelCargoGroupIds));

  // @ts-expect-error this is fine
  const currentSchema = schemaMap[stepIndex];

  const methods = useForm<FinalSchema>({
    mode: "onSubmit",
    resolver: zodResolver(currentSchema),
    values: {
      incoterms: airQuoteRequest.incoterms as IncotermsEnum,
      include_cargo_insurance: airQuoteRequest.insurance_required as boolean,
      include_customs_clearance: airQuoteRequest.include_customs_clearance as boolean,
      is_hazardous: airQuoteRequest.is_hazardous as boolean,
      is_overweight: airQuoteRequest.is_overweight as boolean,
      stops: (airQuoteRequest.stops as LocationSchema["stops"]) ?? [{}, {}],
      deadline_respond_at: airQuoteRequest.deadline_respond_at ?? undefined,
      hazardous_notes: airQuoteRequest.hazardous_goods_details ?? undefined,
      notes: airQuoteRequest.note ?? undefined,
      reference_number: airQuoteRequest.reference_number ?? undefined,
      internal_reference_number: airQuoteRequest.internal_reference_number ?? undefined,
      hide_internal_ref_till_dispatch: airQuoteRequest.hide_internal_ref_till_dispatch ?? false,
      partners: airQuoteRequest.partners ?? [],
      teammates: airQuoteRequest.teammates,
      cargo_groups: (cargoGroups as unknown as CargoGroup[]) ?? [],
      cargo_ready_date: airQuoteRequest.stops?.[0].start as string,
      routing_type: airQuoteRequest.routing_type as RoutingTypeEnum,
      unloading_details: airQuoteRequest.unloading_details as UnloadingDetailsEnum,
      target_delivery_date: airQuoteRequest.stops?.[1].start ?? undefined,
    },
  });

  const onSubmit = () => {
    finalizeAndSendQr();
  };

  if (resetLocalState) {
    return null;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <RoutingStep onSubmit={onSubmit} />
        <ShipmentDetailsStep onSubmit={onSubmit} />
        <PartnersStep onSubmit={onSubmit} />
        <TeamStep onSubmit={onSubmit} />
        <ReviewStep onSubmit={onSubmit} />
      </FormProvider>
    </div>
  );
};

export default QuoteRequestDrayageForm;
